import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { axiosInstance } from "@retro-ia/shared/axios";
import { useClientesFinalesProgramas } from "@/hooks/clientes-finales/useClientesFinales";
import type {
  ClienteFinal,
  ClienteFinalProgramas,
} from "@interfaces/user.interface";
import type { Programa } from "@/interfaces/programa.interface";
import type { ProgramaClienteFinal } from "@/interfaces/programa-cliente-final.interface";

export type ProgramasScope = "all" | "owned";

export interface ProgramasScopeResult {
  clientes: ClienteFinalProgramas[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
}

export interface ProgramasScopeConfig {
  title: string;
  buttonLabel: string;
  supportsPagination: boolean;
  supportsSearch: boolean;
  queryInvalidateKey: ReadonlyArray<unknown>;
  useProgramas: (
    page: number,
    limit: number,
    search: string,
  ) => ProgramasScopeResult;
}

type ProgramasDashboardResponse = {
  data: ClienteFinalProgramas[];
  total: number;
};

const mapProgramaToCard = (
  programa: Programa & { asignacionesCount?: number },
): ProgramaClienteFinal => ({
  id: programa.id,
  idClienteFinal: programa.idClienteFinal,
  nombre: programa.nombre,
  isActivo: programa.isActivo,
  tono: programa.tono,
  cupoMaximo: programa.cupoMaximo,
  cerebro: programa.cerebro ?? "",
  limiteIntentos: programa.limiteIntentos ?? undefined,
  rol: programa.rol,
  restricciones: programa.restricciones,
  formato: programa.formato,
  fechaInicio: programa.fechaInicio,
  fechaFin: programa.fechaFin,
  creadoEl: programa.creadoEl,
  actualizadoEl: programa.actualizadoEl,
  isOculto: programa.isOculto,
  contexto_coach: programa.contexto_coach ?? null,
  rol_coach: programa.rol_coach ?? null,
  tarea_coach: programa.tarea_coach ?? null,
  restricciones_criterios_coach: programa.restricciones_criterios_coach ?? null,
  learnersCount:
    programa.asignacionesCount ?? programa._count?.aprendizPrograma ?? 0,
});

const useAdminProgramasDashboard = (
  page: number,
  limit: number,
  search: string,
): ProgramasScopeResult => {
  const query = useQuery<ProgramasDashboardResponse, Error>({
    queryKey: ["programas-dashboard-admin", page, limit, search],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await axiosInstance.get<{
        data: Programa[];
        total: number;
      }>("/programas", {
        params: { page, limit, search },
      });
      const programas = response.data.data ?? [];
      const clienteIds = Array.from(
        new Set(programas.map((p) => p.idClienteFinal).filter(Boolean)),
      );

      const clienteNombreMap = new Map<number, string>();
      if (clienteIds.length > 0) {
        const clientes = await Promise.all(
          clienteIds.map(async (clienteId) => {
            const clienteResponse = await axiosInstance.get<ClienteFinal>(
              `/clientes-finales/${clienteId}`,
            );
            return clienteResponse.data;
          }),
        );
        clientes.forEach((cliente) => {
          clienteNombreMap.set(
            cliente.id,
            cliente.nombreEmpresa ?? `Cliente Final #${cliente.id}`,
          );
        });
      }

      const grouped = new Map<number, ClienteFinalProgramas>();
      programas.forEach((programa) => {
        const clienteId = programa.idClienteFinal;
        if (!clienteId) return;
        if (!grouped.has(clienteId)) {
          grouped.set(clienteId, {
            id: clienteId,
            nombreEmpresa:
              clienteNombreMap.get(clienteId) ?? `Cliente Final #${clienteId}`,
            programas: [],
          });
        }
        grouped.get(clienteId)!.programas.push(mapProgramaToCard(programa));
      });

      return {
        data: Array.from(grouped.values()),
        total: response.data.total ?? 0,
      };
    },
  });

  return {
    clientes: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
};

const useOwnedProgramasDashboard = (): ProgramasScopeResult => {
  const query = useClientesFinalesProgramas();
  const clientes = (
    Array.isArray(query.data) ? query.data : []
  ) as ClienteFinalProgramas[];
  const total = clientes.reduce(
    (sum, cliente) => sum + cliente.programas.length,
    0,
  );
  return {
    clientes,
    total,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
};

export const PROGRAMAS_SCOPE_CONFIG: Record<
  ProgramasScope,
  ProgramasScopeConfig
> = {
  all: {
    title: "Programas",
    buttonLabel: "+ Registrar programa",
    supportsPagination: true,
    supportsSearch: true,
    queryInvalidateKey: ["programas-dashboard-admin"],
    useProgramas: useAdminProgramasDashboard,
  },
  owned: {
    title: "Programas",
    buttonLabel: "+ Registrar programa",
    supportsPagination: false,
    supportsSearch: false,
    queryInvalidateKey: ["clientesFinalesProgramas"],
    useProgramas: useOwnedProgramasDashboard,
  },
};

export function getProgramasScopeConfig(
  scope: ProgramasScope,
): ProgramasScopeConfig {
  const config = PROGRAMAS_SCOPE_CONFIG[scope];
  if (!config) {
    throw new Error(`Unknown programas scope "${scope}".`);
  }
  return config;
}
